import { apiRequest } from '../api/client';

/**
 * The 11 predefined codes of `apps/api` `ROLE_CATALOG`. The API's read-only
 * protection is keyed on membership in this catalogue, not on the stored
 * `isSystem` flag (`custom-roles/spec.md`, "Los roles de catálogo son de
 * solo lectura en la UI") — this mirrors that same rule on the client so the
 * UI never offers "Editar"/"Eliminar" on a row the server would reject.
 */
export const ROLE_CATALOG_CODES = [
  'SUPER_ADMIN',
  'ADMIN',
  'OPERATIONS',
  'KITCHEN',
  'DELIVERY',
  'BILLING',
  'SALES',
  'AUDITOR',
  'CLIENT_BUYER',
  'CLIENT_MANAGER',
  'CLIENT_VIEWER',
] as const;

export function isCatalogRoleCode(code: string): boolean {
  return (ROLE_CATALOG_CODES as readonly string[]).includes(code);
}

/**
 * Permissions no custom role can ever hold, regardless of the actor's own
 * grants (`escalation-guard.ts` `NEVER_CUSTOM_ROLE_PERMISSIONS`). The role
 * form never renders these as an option — not even disabled.
 */
export const NEVER_CUSTOM_ROLE_PERMISSIONS = ['role.manage', 'user.impersonate', 'fiscal.config', 'email.config'] as const;

/** Matches `apps/api` `ListRolesUseCase`'s `RoleListItem` (`GET /roles`). */
export interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  createdBy: string | null;
  createdAt: string;
  userCount: number;
}

/** Matches `apps/api` `PermissionDefinition` (`GET /roles/permissions`). */
export interface Permission {
  code: string;
  resource: string;
  action: string;
  description: string;
}

/** `GET /roles`, ordered by the API: SUPER_ADMIN, then catalogue, then custom by creation date. */
export function listRoles(): Promise<Role[]> {
  return apiRequest<Role[]>('/roles');
}

/** `GET /roles/permissions`, the full permission catalogue. */
export function listPermissions(): Promise<Permission[]> {
  return apiRequest<Permission[]>('/roles/permissions');
}

export interface CreateRoleInput {
  code: string;
  name: string;
  description?: string;
  permissionCodes: string[];
}

/** `POST /roles`. */
export function createRole(input: CreateRoleInput): Promise<Role> {
  return apiRequest<Role>('/roles', { method: 'POST', body: JSON.stringify(input) });
}

export interface UpdateRoleInput {
  name?: string;
  description?: string | null;
  permissionCodes?: string[];
}

/** `PATCH /roles/:id`. `code` is never sent: the API never accepts it. */
export function updateRole(id: string, input: UpdateRoleInput): Promise<Role> {
  return apiRequest<Role>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

/** `DELETE /roles/:id`. */
export function deleteRole(id: string): Promise<void> {
  return apiRequest<void>(`/roles/${id}`, { method: 'DELETE' });
}
